import Cache from "../Util/Cache";
import CutoutTemplate from "./CutoutTemplate";
import MapImageProvider from "../Projection/MapImageProvider";
import Wms from "../Projection/Wms";
import Wmts from "../Projection/Wmts";
import Paper, {PaperList} from "../Util/Paper";
import {Serialization} from "./Serializer";
import CoordinateSystem from "../Coordinates/CoordinateSystem";
import Coordinate from "../Coordinates/Coordinate";
import CoordinateConverter from "../Util/CoordinateConverter";
import OlMip from "../Projection/OlMip";
import EmptyMip from "../Projection/EmptyMip";
import OlVectorMip from "../Projection/OlVectorMip";
import BolletjePijltje from "../RouteTechniques/BolletjePijltje";
import Bolletjes from "../RouteTechniques/Bolletjes";
import Graden from "../RouteTechniques/Graden";
import Helikopter from "../RouteTechniques/Helikopter";
import Oleaat from "../RouteTechniques/Oleaat";
import Stripkaart from "../RouteTechniques/Stripkaart";
import Vector from "../RouteTechniques/Vector";
import Vliegkoers from "../RouteTechniques/Vliegkoers";
import Kruispuntenroute from "../RouteTechniques/Kruispuntenroute";
import Situatieschets from "../RouteTechniques/Situatieschets";
import {CardinalDirections, CardinalDirectionsLocale} from "../Util/CardinalDirections.js";
import {reactive} from "vue";
import UserInterface from "./UserInterface.js";
import MagneticNorthProvider from "./MagneticNorthProvider.js";

export type ContainerReactiveProps = {
    cardinalDirections: CardinalDirections,
};

export enum ElevationMode {
    AlwaysOn = 'AlwaysOn',
    OptIn = 'OptIn',
    Disabled = 'Disabled',
}

export const ElevationModeOptionLabels = {
    'AlwaysOn' : 'Altijd aan',
    'OptIn' : 'Eenmalig aanzetten per sessie',
    'Disabled' : 'Verbergen',
};

export default class Container {
    static readonly CUSTOM_CUTOUT_TEMPLATE_LOCALSTORAGE_KEY = 'custom_cutout_templates';
    static readonly NEIGHBOUR_OVERLAP_LOCALSTORAGE_KEY = 'neighbour_overlap';
    static readonly DEFAULT_ADVANCED_SETTINGS_MODE_LOCALSTORAGE_KEY = 'default_advanced_settings_mode';
    static readonly DEFAULT_ELEVATION_MODE_LOCALSTORAGE_KEY = 'default_elevation_mode';
    static readonly CARDINAL_DIRECTIONS_LOCALSTORAGE_KEY = 'cardinal_directions';

    static readonly PREFERRED_COORDSYSTEM_LOCALSTORAGE_KEY = 'coord_panel_preferred_coordsystem';
    static readonly PREFERRED_FORMATS_LOCALSTORAGE_KEY = 'coord_panel_preferred_formats';
    static readonly COORDSYSTEMS_IN_PANEL_LOCALSTORAGE_KEY = 'coord_panel_coordsystems';

    private static userInterface: UserInterface = null;
    private static mapImageProviders: Record<string, MapImageProvider> = {};
    private static interfaceMapImageProviders: Record<string, MapImageProvider> = {};

    private static paperList: PaperList = new PaperList();
    private static cardinalDirections: CardinalDirections = null;

    private static systemCutoutTemplates: CutoutTemplate<any, any, any>[] = [];
    private static customCutoutTemplates: CutoutTemplate<any, any, any>[] = [];

    private static cache: Cache = null;

    private static reactiveProps: ContainerReactiveProps = null;

    private static _magneticNorthProvider: MagneticNorthProvider|null = null;

    static setUserInterface(userInterface: UserInterface): void {
        if (Container.userInterface !== null) {
            throw new Error('User interface is already initialized');
        }

        Container.userInterface = userInterface;
    }

    static getReactiveProps(): ContainerReactiveProps {
        if (Container.reactiveProps == null) {
            Container.reactiveProps = <ContainerReactiveProps>reactive({
                cardinalDirections: null,
            });
            Container.buildCardinalDirections();
        }
        return Container.reactiveProps;
    }

    static registerMapImageProvider(mapImageProvider: MapImageProvider) {
        Container.mapImageProviders[mapImageProvider.name] = mapImageProvider;
    }

    static registerInterfaceMips(mapImageProviderNames: string[]) {
        for (const mapImageProviderName of mapImageProviderNames) {
            Container.interfaceMapImageProviders[mapImageProviderName] = Container.mapImageProviders[mapImageProviderName];
        }
    }

    static mapImageProvider(name: string): MapImageProvider {
        if(!Container.mapImageProviders.hasOwnProperty(name)) {
            throw new Error('Unknown map image provider "' + name + '"');
        }

        return Container.mapImageProviders[name];
    }

    static wms(wmsName: string): Wms {
        const wms = Container.mapImageProvider(wmsName);
        if(!(wms instanceof Wms)) {
            throw new Error('Map image provider "' + name + '" is not WMS');
        }
        return wms;
    }

    static wmts(wmtsName: string): Wmts {
        const wms = Container.mapImageProvider(wmtsName);
        if(!(wms instanceof Wmts)) {
            throw new Error('Map image provider "' + wmtsName + '" is not WMTS');
        }
        return wms;
    }

    static olMip(olMipName: string): OlMip {
        const olMip = Container.mapImageProvider(olMipName);
        if(!(olMip instanceof OlMip)) {
            throw new Error('Map image provider "' + olMipName + '" is not an OpenLayers Map Image Provider');
        }
        return olMip;
    }

    static olVectorMip(olVectorMipName: string): OlVectorMip {
        const olMip = Container.mapImageProvider(olVectorMipName);
        if(!(olMip instanceof OlVectorMip)) {
            throw new Error('Map image provider "' + olVectorMipName + '" is not an OpenLayers Vector Map Image Provider');
        }
        return olMip;
    }

    static emptyMip(emptyMipName: string): EmptyMip {
        const emptyMip = Container.mapImageProvider(emptyMipName);
        if(!(emptyMip instanceof EmptyMip)) {
            throw new Error('Map image provider "' + emptyMipName + '" is not an Empty Map Image Provider');
        }
        return emptyMip;
    }

    static mapImageProviderList(): MapImageProvider[] {
        return Object.values(Container.mapImageProviders);
    }

    static interfaceMapImageProviderList(): MapImageProvider[] {
        return Object.values(Container.interfaceMapImageProviders);
    }

    static registerSystemPaper(paper: Paper): void {
        Container.paperList.registerSystemPaper(paper);
    }

    static getPaperList(): PaperList {
        return Container.paperList;
    }

    static registerSystemCutoutTemplate(cutoutTemplate: CutoutTemplate<any, any, any>) {
        Container.systemCutoutTemplates.push(cutoutTemplate);
    }

    static registerCustomCutoutTemplate(cutoutTemplate: CutoutTemplate<any, any, any>) {
        Container.customCutoutTemplates.push(cutoutTemplate);
    }

    static getSerializedCustomCutoutTemplatesFromStorage(): Record<string, Serialization> {
        const entry = window.localStorage.getItem(Container.CUSTOM_CUTOUT_TEMPLATE_LOCALSTORAGE_KEY);
        if(entry === null) {
            return {};
        }
        return <Record<string, Serialization>>JSON.parse(entry);
    }

    static registerCustomCutoutTemplates(): void {
        const serializedTemplates = Container.getSerializedCustomCutoutTemplatesFromStorage();
        for(const name in serializedTemplates) {
            const serialized = serializedTemplates[name];
            const cutoutTemplate = CutoutTemplate.unserialize(serialized);
            Container.registerCustomCutoutTemplate(cutoutTemplate);
        }
    }

    static addCustomCutoutTemplate(cutoutTemplate: CutoutTemplate<any, any, any>) {
        Container.customCutoutTemplates.push(cutoutTemplate);

        const serializedTemplates = Container.getSerializedCustomCutoutTemplatesFromStorage();
        serializedTemplates[cutoutTemplate.name] = cutoutTemplate.serialize();
        window.localStorage.setItem(Container.CUSTOM_CUTOUT_TEMPLATE_LOCALSTORAGE_KEY, JSON.stringify(serializedTemplates));
    }

    static removeCustomCutoutTemplate(cutoutTemplate: CutoutTemplate<any, any, any>) {
        const index = Container.customCutoutTemplates.indexOf(cutoutTemplate);
        if(index > -1) {
            Container.customCutoutTemplates.splice(index, 1);
        }

        const serializedTemplates = Container.getSerializedCustomCutoutTemplatesFromStorage();
        delete serializedTemplates[cutoutTemplate.name];
        window.localStorage.setItem(Container.CUSTOM_CUTOUT_TEMPLATE_LOCALSTORAGE_KEY, JSON.stringify(serializedTemplates));
    }

    static cutoutTemplateList(): CutoutTemplate<any, any, any>[] {
        return [...Container.systemCutoutTemplates, ...Container.customCutoutTemplates];
    }

    static systemCutoutTemplateList(): CutoutTemplate<any, any, any>[] {
        return Container.systemCutoutTemplates;
    }

    static customCutoutTemplateList(): CutoutTemplate<any, any, any>[] {
        return Container.customCutoutTemplates;
    }

    static setNeighbourOverlap(overlap: number) {
        window.localStorage.setItem(Container.NEIGHBOUR_OVERLAP_LOCALSTORAGE_KEY, overlap.toString());
    }

    static getNeighbourOverlap(): number {
        const overlap = window.localStorage.getItem(Container.NEIGHBOUR_OVERLAP_LOCALSTORAGE_KEY);
        if(overlap === null) {
            return 0;
        }
        return parseInt(overlap);
    }

    static getCardinalDirections(): CardinalDirections {
        if (Container.cardinalDirections == null) {
            Container.buildCardinalDirections();
        }

        return Container.cardinalDirections;
    }

    private static buildCardinalDirections() {
        Container.getReactiveProps().cardinalDirections = Container.cardinalDirections = new CardinalDirections(
            Container.getCardinalDirectionsCount(),
            Container.getCardinalDirectionsLocale(),
        );
    }

    static setCardinalDirectionsConfig(count: number, locale: string) {
        if (!CardinalDirections.VALID_COUNTS.includes(count) || typeof CardinalDirections.LOCALES[locale] === 'undefined') {
            throw new Error('Invalid input ' + count + '/' + locale);
        }

        window.localStorage.setItem(Container.CARDINAL_DIRECTIONS_LOCALSTORAGE_KEY, JSON.stringify({
            count: count.toString(),
            locale: locale,
        }));

        Container.buildCardinalDirections();
    }

    static getCardinalDirectionsCount(): number {
        const cardinalDirectionsConfig = window.localStorage.getItem(Container.CARDINAL_DIRECTIONS_LOCALSTORAGE_KEY);
        if (cardinalDirectionsConfig === null) {
            return CardinalDirections.DEFAULT_COUNT;
        }
        return parseInt(JSON.parse(cardinalDirectionsConfig).count);
    }

    static getCardinalDirectionsLocale(): CardinalDirectionsLocale {
        const cardinalDirectionsConfig = window.localStorage.getItem(Container.CARDINAL_DIRECTIONS_LOCALSTORAGE_KEY);
        if (cardinalDirectionsConfig === null) {
            return CardinalDirections.DEFAULT_LOCALE;
        }
        return JSON.parse(cardinalDirectionsConfig).locale;
    }

    static setDefaultAdvancedSettingsMode(defaultAdvancedSettingsMode: boolean): void {
        window.localStorage.setItem(Container.DEFAULT_ADVANCED_SETTINGS_MODE_LOCALSTORAGE_KEY, defaultAdvancedSettingsMode ? '1' : '0');
    }

    static getDefaultAdvancedSettingsMode(): boolean {
        return window.localStorage.getItem(Container.DEFAULT_ADVANCED_SETTINGS_MODE_LOCALSTORAGE_KEY) === '1';
    }

    static setDefaultElevationMode(defaultElevationMode: string): void {
        window.localStorage.setItem(Container.DEFAULT_ELEVATION_MODE_LOCALSTORAGE_KEY, defaultElevationMode);

        Container.userInterface.getMap().updateElevationMode();
    }

    static getDefaultElevationMode(): ElevationMode {
        return ElevationMode[window.localStorage.getItem(Container.DEFAULT_ELEVATION_MODE_LOCALSTORAGE_KEY)] || ElevationMode.OptIn;
    }

    static setPreferredCoordinateSystem(coordinateSystem: CoordinateSystem<Coordinate>) {
        window.localStorage.setItem(Container.PREFERRED_COORDSYSTEM_LOCALSTORAGE_KEY, coordinateSystem.code);
    }

    static setPreferredCoordinateFormatName(coordinate: Coordinate, formatName: string) {
        const formats = coordinate.formats();
        if(formats.hasOwnProperty(formatName)) {
            const preferredFormats = Container.getPreferredCoordinateFormats();
            preferredFormats[coordinate.code] = formatName;
            window.localStorage.setItem(Container.PREFERRED_FORMATS_LOCALSTORAGE_KEY, JSON.stringify(preferredFormats));
        }
    }

    static getPreferredCoordinateSystem(): CoordinateSystem<Coordinate> {
        const code = window.localStorage.getItem(Container.PREFERRED_COORDSYSTEM_LOCALSTORAGE_KEY);
        if(code === null) {
            return CoordinateConverter.getCoordinateSystem('EPSG:28992');
        } else {
            return CoordinateConverter.getCoordinateSystem(code);
        }
    }

    static getPreferredCoordinateFormats(): Record<string, string> {
        let preferredFormats = window.localStorage.getItem(Container.PREFERRED_FORMATS_LOCALSTORAGE_KEY);
        if(preferredFormats === null) {
            return {};
        } else {
            return JSON.parse(preferredFormats);
        }
    }

    static setCoordinateSystemsInPanelDynamic(dynamic: boolean) {
        let settings;
        if (dynamic) {
            settings = null;
        } else {
            settings = Container.computeDynamicCoordinateSystemsInPanel();
        }
        window.localStorage.setItem(Container.COORDSYSTEMS_IN_PANEL_LOCALSTORAGE_KEY, JSON.stringify(settings));
    }

    static setCoordinateSystemInPanel(coordinateSystem: CoordinateSystem<Coordinate>, enable: boolean) {
        const settings = Container.getCoordinateSystemsInPanel().coordinateSystemsInPanel;
        if (enable) {
            if (!settings.includes(coordinateSystem.code)) {
                settings.push(coordinateSystem.code);
            }
        } else {
            if (settings.includes(coordinateSystem.code)) {
                settings.splice(settings.indexOf(coordinateSystem.code), 1);
            }
        }
        window.localStorage.setItem(Container.COORDSYSTEMS_IN_PANEL_LOCALSTORAGE_KEY, JSON.stringify(settings));
    }

    static getCoordinateSystemsInPanel(): {coordinateSystemsInPanel: string[], dynamic: boolean} {
        let settings = window.localStorage.getItem(Container.COORDSYSTEMS_IN_PANEL_LOCALSTORAGE_KEY);
        if (settings !== null) {
            settings = <string[]|null>JSON.parse(settings);
        }

        if (settings === null) {
            return {coordinateSystemsInPanel: Container.computeDynamicCoordinateSystemsInPanel(), dynamic: true};
        } else {
            return {coordinateSystemsInPanel: settings, dynamic: false};
        }
    }

    private static computeDynamicCoordinateSystemsInPanel(): string[] {
        const preferredCoordinateSystem = Container.getPreferredCoordinateSystem();

        const coordinateSystemsRecord: Record<string, boolean> = {};

        coordinateSystemsRecord['EPSG:4326'] = true; // Always show WGS84
        coordinateSystemsRecord[preferredCoordinateSystem.code] = true;
        for(const cutout of Container.userInterface.getCutouts()) {
            coordinateSystemsRecord[cutout.workspaceCoordinateSystem.code] = true;
            coordinateSystemsRecord[cutout.getProjection().coordinateSystem.code] = true;
            coordinateSystemsRecord[cutout.getGrid().coordinateSystem.code] = true;
        }

        return Object.keys(coordinateSystemsRecord);
    }

    static getCache(): Promise<Cache> {
        return new Promise((resolve, reject) => {
            if(Container.cache !== null) {
                resolve(Container.cache);
            } else {
                const cache = new Cache();
                cache.initialize().then(() => {
                    Container.cache = cache;
                    resolve(cache);
                });
            }
        });
    }

    static clearCaches(): Promise<void> {
        return new Promise((resolve, reject) => {
            Container.getCache().then((cache) => {
                return cache.clear().then(resolve, reject);
            });
        }).then(() => {
            alert('De buffers zijn geleegd');
        }).catch((...args) => {
            console.log(...args);
            alert('Something went wrong clearing the cache');
        });
    }

    static resetStorage(): void {
        window.localStorage.clear();
        Container.customCutoutTemplates = [];
        alert('De voorkeuren zijn gewist');
    }

    static resetApplication(): Promise<void> {
        return new Promise((resolve, reject) => {
            window.localStorage.clear();
            Container.customCutoutTemplates = [];

            if(Container.cache !== null) {
                Container.cache.close();
                Container.cache = null;
            }

            Cache.drop().then(resolve, reject);
        }).then(() => {
            alert('De applicatie is gewist. De applicatie zal nu opnieuw geopend worden.');
            window.location.reload();
        }).catch((...args) => {
            console.log(...args);
            alert('Something went wrong clearing the cache');
        });
    }

    public static allRouteTechniques() {
        const techniques = {};
        techniques[BolletjePijltje.TECHNIQUE_NAME] = BolletjePijltje;
        techniques[Bolletjes.TECHNIQUE_NAME] = Bolletjes;
        techniques[Graden.TECHNIQUE_NAME] = Graden;
        techniques[Helikopter.TECHNIQUE_NAME] = Helikopter;
        techniques[Kruispuntenroute.TECHNIQUE_NAME] = Kruispuntenroute;
        techniques[Oleaat.TECHNIQUE_NAME] = Oleaat;
        techniques[Situatieschets.TECHNIQUE_NAME] = Situatieschets;
        techniques[Stripkaart.TECHNIQUE_NAME] = Stripkaart;
        techniques[Vector.TECHNIQUE_NAME] = Vector;
        techniques[Vliegkoers.TECHNIQUE_NAME] = Vliegkoers;
        return techniques;
    }

    public static async getMagneticNorthProvider(): Promise<MagneticNorthProvider> {
        if (Container._magneticNorthProvider === null) {
            Container._magneticNorthProvider = new MagneticNorthProvider(await Container.getCache());
        }

        await Container._magneticNorthProvider.awaitInit();

        return Container._magneticNorthProvider;
    }
}
